import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Stack, Alert } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { SPACING } from '@common/theme/spacing'
import { SIZING } from '@common/theme/sizing'
import { useGetTimePointSubmissionDetails } from '@features/subject/hooks/useGetTimePointSubmissionDetails'
import {
  useGetSubjectById,
  useGetSubjectTimepoints,
} from '@features/study_details/Subjects/hooks/useStudySubjectsQueries'
import { useGetStudySubjectConfiguration } from '@study_setup/SubjectConfiguration/hooks/useStudySubjectConfigurationQueries'
import { SubjectsTimepointSchema, TimepointSubmissionStatus } from '@common/config/api/client/types.gen'
import { useForm, FormProvider } from 'react-hook-form'
import { SubjectDetailsFormValues } from '@features/subject/subject.types'
import { useLoadStudySubjectDetails } from '@features/study_details/Subjects/hooks/useLoadStudySubjectDetails'
import SubjectTimePointConfiguration from '../SubjectTimePointConfiguration/index'
import TimePointManager from '../TimePointManager/index'
import { findSubmissionById } from '../../utils'
import {
  getViewableSubmissions,
  useOpenAllTimepointsInViewer,
} from '@features/subject/hooks/useOpenAllTimepointsInViewer'
import { useEffect } from 'react'
import { generatePathWithParams } from '@common/utils/urlUtils'
import { ROUTES } from 'src/routes'
import { useTheme } from '@mui/material/styles'
import { useCombinedPermissions } from '@auth/hooks/useCombinedPermissions'
import { SitePermission } from '@auth/permissionsEnum'

const SUBMITTED_VISIT_STATUSES = new Set<TimepointSubmissionStatus>(['COMPLETE', 'LOCKED'])

const toTimePointGroups = (data: SubjectsTimepointSchema | undefined, subjectId?: string, submittedOnly = false) => {
  const subject = data?.subjects?.find((item) => item.subject_id === subjectId)
  const groups =
    subject?.study_timepoints?.map((timepoint) => ({
      name: timepoint.name,
      id: timepoint.timepoint_id,
      maximumAllowedTimepoints: timepoint.max_number_of_timepoint_submissions,
      children:
        timepoint.timepoint_submissions
          ?.filter((submission) => !submittedOnly || SUBMITTED_VISIT_STATUSES.has(submission.status))
          .map((submission) => ({
            name: submission.name,
            id: submission.timepoint_submission_id,
          })) || [],
    })) || []

  return submittedOnly ? groups.filter((group) => group.children.length > 0) : groups
}

export default function SubjectTimepointSubmission() {
  const navigate = useNavigate()
  const theme = useTheme()

  const { studyId, subjectId, timepointSubmissionId } = useParams<{
    studyId: string
    subjectId: string
    timepointSubmissionId?: string
  }>()

  const {
    data: subjectData,
    isError,
    isLoading: isSubjectLoading,
  } = useGetSubjectById({
    study_id: studyId!,
    subject_id: subjectId!,
  })

  const [searchParams, setSearchParams] = useSearchParams()

  const { data: studySubjectConfigData, isLoading: isSubjectConfigLoading } = useGetStudySubjectConfiguration(studyId!)

  const { globalPermissions, studyPermissions } = useCombinedPermissions()
  const submittedOnlyLocked = [globalPermissions, studyPermissions].some((permissions) =>
    permissions.includes(SitePermission.VIEW_SUBMITTED_VISITS_ONLY),
  )
  const submittedOnly = submittedOnlyLocked || searchParams.get('submitted_only') === 'true'

  const handleSubmittedOnlyChange = (checked: boolean) => {
    const next = new URLSearchParams(searchParams)
    if (checked) {
      next.set('submitted_only', 'true')
    } else {
      next.delete('submitted_only')
    }
    setSearchParams(next, { replace: true })
  }

  const { data: subjectTimepointsData, isLoading: isSubjectTimepointsLoading } = useGetSubjectTimepoints({
    study_id: studyId!,
    subject_id: subjectId!,
    submitted_only: submittedOnly || undefined,
  })

  const { data: timePointSubmissionData, isLoading: isTimePointSubmissionLoading } = useGetTimePointSubmissionDetails(
    timepointSubmissionId ? ['TimePointsDetails', timepointSubmissionId] : [],
    {
      study_id: studyId!,
      subject_id: subjectId!,
      timepoint_submission_id: timepointSubmissionId!,
    },
  )

  const methods = useForm<SubjectDetailsFormValues>()
  useLoadStudySubjectDetails(methods, studyId!, subjectData, studySubjectConfigData)
  const viewableSubmissions = getViewableSubmissions(subjectTimepointsData, subjectId).filter(
    (submission) => !submittedOnly || submission.isViewable,
  )
  const { handleOpenAllInViewer, modalityOptions } = useOpenAllTimepointsInViewer(
    studyId,
    subjectId,
    viewableSubmissions,
  )

  const timePoints = toTimePointGroups(subjectTimepointsData, subjectId, submittedOnly)
  const visibleVisitKey = timePoints.flatMap((timepoint) => timepoint.children.map((visit) => visit.id)).join('|')
  const openVisitIsVisible = Boolean(
    timepointSubmissionId && visibleVisitKey.split('|').includes(timepointSubmissionId),
  )

  useEffect(() => {
    if (isSubjectTimepointsLoading || !subjectTimepointsData) return
    if (openVisitIsVisible) return
    if (timepointSubmissionId && !submittedOnly) return

    const nextVisitId = visibleVisitKey.split('|')[0]
    if (!nextVisitId || nextVisitId === timepointSubmissionId) return

    const path = generatePathWithParams(ROUTES.SUBJECT_TIMEPOINT_SUBMISSION.path, {
      studyId,
      subjectId,
      timepointSubmissionId: nextVisitId,
    })
    navigate({ pathname: path, search: `?${searchParams.toString()}` }, { replace: true })
  }, [
    openVisitIsVisible,
    timepointSubmissionId,
    isSubjectTimepointsLoading,
    subjectTimepointsData,
    submittedOnly,
    visibleVisitKey,
    studyId,
    subjectId,
    navigate,
    searchParams,
  ])

  if (isSubjectLoading || isSubjectConfigLoading || (isSubjectTimepointsLoading && !subjectTimepointsData)) {
    return <div>Loading...</div>
  }

  if (isError) {
    return <div data-testid="error-message">Failed to load subject</div>
  }

  const hasAnySubmissions = timePoints.some((tp) => tp.children && tp.children.length > 0)

  let mainContent

  if (timepointSubmissionId && openVisitIsVisible && isTimePointSubmissionLoading) {
    mainContent = <div>Loading timepoint submission details...</div>
  } else if (!hasAnySubmissions) {
    mainContent = (
      <Alert
        severity="info"
        variant="outlined"
        sx={() => ({
          mt: 2,
        })}
      >
        There are no timepoint submissions available for this subject. You can create new submissions using the
        timepoints panel.
      </Alert>
    )
  } else if (!timepointSubmissionId || !openVisitIsVisible) {
    mainContent = <div>Loading available timepoint submissions...</div>
  } else {
    const selectedSubmission = findSubmissionById(
      subjectTimepointsData?.subjects?.find((x) => x.subject_id === subjectId)?.study_timepoints || [],
      timepointSubmissionId!,
    )

    if (!selectedSubmission) {
      mainContent = (
        <Alert
          severity="error"
          sx={{
            mt: 2,
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.error.light,
            color: theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.dark,
            border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.error.main : theme.palette.error.light}`,
          }}
        >
          The requested timepoint submission could not be found.
        </Alert>
      )
    } else {
      const containerRequirements = timePointSubmissionData?.data?.timepoint_submission.container_requirements || []

      const mergedFileTypes = Array.from(new Set(containerRequirements.flatMap((item) => item.file_types) || []))
      const mergedModalities = Array.from(
        new Set(containerRequirements.flatMap((item) => (item.modality ? [item.modality] : []))),
      )

      const containerParam =
        containerRequirements.map((item) => {
          const containerSubmission = timePointSubmissionData?.data?.timepoint_submission.container_submissions.find(
            (req) => req.container_requirement_id === item.container_requirement_id,
          )

          const transformedExamMetadata = containerSubmission?.exam_metadata?.map((exam) => ({
            ...exam,
            series: exam.series.map((series) => ({
              ...series,
              qc_series_tags: series.qc_series_tags.map((tag) => ({
                ...tag,
                apply_to_all_series: tag.apply_to_all_series ?? false,
                allow_apply_to_all_series: tag.allow_apply_to_all_series ?? false,
                dicom_tag_id: tag.dicom_tag_id || '',
                original_value: tag.original_value || '',
                new_value: tag.new_value || '',
              })),
            })),
          }))

          return {
            name: item.name,
            type: item.container_type,
            isOptional: item.is_optional || false,
            modality: item.modality as string,
            convertToDicom: item.convert_to_dicom,
            containerSubmissionId: containerSubmission?.container_submission_id || '',
            eFormFields: item.timepoint_container_eform_fields,
            eFormValues: containerSubmission?.container_eform_submission,
            status: containerSubmission?.status,
            noFilesSubmitted: !!containerSubmission?.no_file_submission_reason,
            criteriaCheck: containerSubmission?.criteria_check,
            examMetadata: transformedExamMetadata,
            comments: containerSubmission?.comments,
            noFileSubmissionReason: containerSubmission?.no_file_submission_reason,
            fileTypesAllowed: mergedFileTypes,
            containerFileTypes: item.file_types,
            modalityAllowed: mergedModalities,
            additionalComments: containerSubmission?.additional_comments,
          }
        }) || []

      mainContent = (
        <Stack spacing={SPACING.sectionsSpacing}>
          <SubjectTimePointConfiguration
            key={timepointSubmissionId}
            timePointFormId={
              timePointSubmissionData?.data?.timepoint_submission.timepoint_requirement?.timepoint_form_id
            }
            eFormFields={
              timePointSubmissionData?.data?.timepoint_submission.timepoint_requirement?.timepoint_form_fields
            }
            subjectData={subjectData}
            eFormValues={timePointSubmissionData?.data?.timepoint_submission.timepoint_form_submission}
            containerParam={containerParam}
            timePoints={timePoints}
            status={selectedSubmission?.status}
            name={selectedSubmission?.name || ''}
          />
        </Stack>
      )
    }
  }

  return (
    <FormProvider {...methods}>
      <Grid container columnSpacing={SPACING.spacingLg}>
        <Grid size={SIZING.gridSizeLg}>{mainContent}</Grid>
        <Grid size={4}>
          <TimePointManager
            timePoints={timePoints}
            selectedTimePointSubmission={timepointSubmissionId || ''}
            viewableSubmissions={viewableSubmissions}
            modalityOptions={modalityOptions}
            onOpenAllInViewer={handleOpenAllInViewer}
            submittedOnly={submittedOnly}
            submittedOnlyLocked={submittedOnlyLocked}
            onSubmittedOnlyChange={handleSubmittedOnlyChange}
          />
        </Grid>
      </Grid>
    </FormProvider>
  )
}
